import React from 'react';
import { Skeleton } from './Skeleton';

/**
 * Generic content placeholder for Container's main column while
 * portal data (mePortal, /me/clinics) is in flight. Mirrors the
 * "Home dashboard" shape — eyebrow + heading, hero band, tile grid,
 * exercise list — so the swap to real content doesn't reflow the
 * page or feel like a hard cut from a branded gradient.
 *
 * Stays generic on purpose: the user's actual landing route (Home,
 * Appointments, etc.) only resolves once portal data is loaded, so
 * we don't yet know which route's specific skeleton to show.
 */
const MainContentSkeleton: React.FC = () => (
  <div className="flex flex-col gap-6" role="status" aria-label="Loading">
    {/* Hero band — matches Home's split-panel hero */}
    <div className="rounded-3xl overflow-hidden md:grid md:grid-cols-5 ring-1 ring-gray-950/5 dark:ring-white/10">
      <div className="p-5 sm:p-6 md:p-7 md:col-span-3 flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 sm:h-8 w-3/4 max-w-xs" />
        </div>
        <Skeleton className="h-11 w-32 rounded-full md:mt-auto" />
      </div>
      <div className="hidden md:flex md:col-span-2 p-7 bg-white dark:bg-[var(--color-bg-card)] items-center gap-4">
        <Skeleton className="size-16 rounded-2xl shrink-0" />
        <div className="flex-1 flex flex-col gap-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    </div>

    {/* 4-tile quick-link row (only at sm+, mirroring Home) */}
    <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="portal-card flex flex-col gap-3 p-4">
          <Skeleton className="size-10 rounded-xl" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>

    {/* List section — matches Today's Exercises preview */}
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-12" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="portal-card flex items-center gap-3 p-3.5">
            <Skeleton className="size-12 rounded-xl shrink-0" />
            <div className="flex-1 flex flex-col gap-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </section>

    <span className="sr-only">Loading…</span>
  </div>
);

export default MainContentSkeleton;
